<template>
  <div class="sc">
    <div class="sc-status">
      <span class="sc-dot"></span>
      <span>SESSION ENDED</span>
    </div>
    <h2 class="sc-title">This session was ended by {{ endedBy }}</h2>
    <p class="sc-meta">{{ meta }}</p>

    <div class="sc-rate-label">QUICK RATE</div>
    <div class="sc-stars">
      <button
        v-for="n in 5"
        :key="n"
        type="button"
        class="sc-star"
        :class="{ filled: n <= rating, hover: n <= hover }"
        @click="setRating(n)"
        @mouseenter="hover = n"
        @mouseleave="hover = 0"
        :aria-label="`Rate ${n} star${n > 1 ? 's' : ''}`"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linejoin="round"
        >
          <polygon
            points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5 12 2"
          />
        </svg>
      </button>
    </div>

    <button
      v-if="showDetailedFeedback"
      class="sc-feedback"
      type="button"
      @click="$emit('feedback')"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        />
      </svg>
      Send detailed feedback
    </button>

    <div v-if="showDetailedFeedback" class="sc-or">
      <span>OR</span>
    </div>

    <button class="sc-back" type="button" @click="$emit('back')">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
      </svg>
      {{ backLabel }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'SessionCard',
  props: {
    endedBy: { type: String, default: 'Host' },
    meta: { type: String, default: '' },
    initialRating: { type: Number, default: 0 },
    showDetailedFeedback: { type: Boolean, default: false },
    backLabel: { type: String, default: 'Back to Dashboard' },
  },
  data() {
    return {
      rating: this.initialRating,
      hover: 0,
    };
  },
  methods: {
    setRating(n) {
      this.rating = n;
      this.$emit('rate', n);
    },
  },
};
</script>

<style scoped>
.sc {
  background: #0e0f17;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 26px 24px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
  display: flex;
  flex-direction: column;
}
.sc-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
}
.sc-dot {
  width: 8px;
  height: 8px;
  background: #4ade80;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(74, 222, 128, 0.6);
}
.sc-title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 14px 0 8px;
  line-height: 1.3;
  overflow-wrap: break-word;
  word-wrap: break-word;
}
.sc-meta {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 28px;
  line-height: 1.5;
  overflow-wrap: break-word;
}
.sc-rate-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  margin-bottom: 10px;
}
.sc-stars {
  display: flex;
  gap: 4px;
  margin-bottom: 22px;
}
.sc-star {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: rgba(255, 255, 255, 0.18);
  transition: color 0.15s ease, transform 0.15s ease;
}
.sc-star:hover {
  transform: scale(1.05);
}
.sc-star.filled,
.sc-star.hover {
  color: #d3ca57;
}
.sc-feedback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.sc-feedback:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.18);
}
.sc-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
}
.sc-or::before,
.sc-or::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}
.sc-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease;
}
.sc-back:hover {
  color: #fff;
}
@media (max-width: 540px) {
  .sc {
    padding: 22px 18px;
  }
  .sc-title {
    font-size: 19px;
  }
  .sc-meta {
    font-size: 12px;
    margin-bottom: 22px;
  }
  .sc-stars {
    margin-bottom: 18px;
  }
}
</style>
